import React, { useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { Dialog } from '@mui/material';
import { Check, MapPin, Search } from 'lucide-react';
import '../../modules/shared/theme/theme.scss';
import './BillingStatePrompt.scss';

export interface BillingStateOption {
  id: number;
  name: string;
}

/**
 * The checkout's "Select your state" step (PaymentService ensureBillingState):
 * shown before an Indian purchase when the account's own location has no state,
 * which its GST invoice needs. Resolves with the picked state id, or null when
 * closed — the payment then doesn't start. The app's sheet (BillingStateHost) in
 * the site's design: icon · title · note · searchable list · primary · "Not now".
 */
function BillingStateDialog({ states, onDone }: { states: BillingStateOption[]; onDone: (stateId: number | null) => void }) {
  const [picked, setPicked] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(true);
  const finish = (stateId: number | null) => {
    setOpen(false);
    onDone(stateId);
  };
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return states.filter((s) => !q || s.name.toLowerCase().includes(q));
  }, [states, query]);

  return (
    <Dialog
      open={open}
      onClose={() => finish(null)}
      fullWidth
      maxWidth="xs"
      aria-labelledby="billing-state-title"
      PaperProps={{ className: 'billing-state__paper' }}
    >
      <div className="brd billing-state">
        <span className="billing-state__icon" aria-hidden><MapPin size={22} /></span>
        <h2 id="billing-state-title" className="billing-state__title">Select your state</h2>
        <p className="billing-state__body">Your state is needed for the GST invoice of this purchase. You only need to add it once.</p>

        <label className="billing-state__search">
          <Search size={16} aria-hidden />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search state" aria-label="Search state" autoFocus />
        </label>

        <div className="billing-state__list" role="radiogroup" aria-label="States">
          {shown.length ? (
            shown.map((s) => {
              const on = s.id === picked;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={`billing-state__row${on ? ' billing-state__row--on' : ''}`}
                  onClick={() => setPicked(s.id)}
                >
                  <span>{s.name}</span>
                  {on && <Check size={16} />}
                </button>
              );
            })
          ) : (
            <p className="billing-state__empty">No state matches “{query.trim()}”</p>
          )}
        </div>

        <button
          type="button"
          className="brd-btn brd-btn--primary billing-state__continue"
          disabled={!picked}
          onClick={() => picked && finish(picked)}
        >
          Continue to payment
        </button>
        <button type="button" className="billing-state__later" onClick={() => finish(null)}>Not now</button>
      </div>
    </Dialog>
  );
}

/** Opens the dialog outside the page's React tree (PaymentService is not a component). */
export function askBillingState(states: BillingStateOption[]): Promise<number | null> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.appendChild(host);
    const root = createRoot(host);
    const onDone = (stateId: number | null) => {
      resolve(stateId);
      // Let the dialog's close transition finish before removing it.
      window.setTimeout(() => {
        root.unmount();
        host.remove();
      }, 300);
    };
    root.render(<BillingStateDialog states={states} onDone={onDone} />);
  });
}
